import {
  useState,
  useMemo,
  type RefObject,
  useRef,
  useEffect,
  type FocusEventHandler,
} from 'react';

import {
  validatePhoneNumberLength,
  type CountryCode,
  AsYouType,
  parsePhoneNumber,
  parseIncompletePhoneNumber,
  formatIncompletePhoneNumber,
} from 'libphonenumber-js/max';

import {
  isCountryCode,
  sanitizeDialCode,
  getDialCode,
  isValidDialCodeFormat,
  type CountryOption,
  CUSTOM_CODE_TO_ISO_CODE_MAP,
} from '../utils';
import {
  calculateCursorPosition,
  isSpaceDeleted,
  useCursorPosition,
  countDigitsBeforeCursor,
} from './useCursorPosition';

// Helper functions
const formatPhoneNumber = (
  phoneNumber: string,
  originalCountryCode: CountryCode | (string & {}) // eslint-disable-line @typescript-eslint/ban-types
): string => {
  try {
    const effectiveCountryCode =
      CUSTOM_CODE_TO_ISO_CODE_MAP[originalCountryCode] || originalCountryCode;

    const parsedPhoneNumber = parsePhoneNumber(
      phoneNumber,
      effectiveCountryCode as CountryCode
    );
    return parsedPhoneNumber
      ? parsedPhoneNumber.formatInternational()
      : phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
};

const filterNonNumeric = (input: string): string => input.replace(/\D/g, '');

// Helper to check if formatting is possible for a given country
const canFormatForCountry = (country?: CountryOption): boolean => {
  if (!country) {
    return false;
  }
  // Ensure country.code is a string before passing to isCountryCode
  const countryCodeStr = typeof country.code === 'string' ? country.code : '';
  const validCode = isCountryCode(countryCodeStr);
  const validDialCode = isValidDialCodeFormat(getDialCode(country));
  return validCode && validDialCode;
};

// Helper to determine country from input value
const findCountryFromInput = (
  inputValue: string,
  countries: CountryOption[]
): { country?: CountryOption; preFormattedValue: string } => {
  const asYouType = new AsYouType();
  // AsYouType might format/clean the input (e.g. remove invalid chars)
  const preFormattedValue = asYouType.input(inputValue);

  const country =
    countries.find(({ code }) => asYouType.getCountry() === code) ??
    countries.find((c) => {
      const dialCode = getDialCode(c);
      return (
        isValidDialCodeFormat(dialCode) &&
        preFormattedValue.startsWith(sanitizeDialCode(dialCode))
      );
    });
  return { country, preFormattedValue };
};

/**
 * Resolves the initial value for the phone input
 */
const resolveInitialValues = (params: {
  countries: CountryOption[];
  defaultCountry?: string;
  inputValue?: string;
}): { initialCountry: CountryOption; formattedValue: string } => {
  const { countries, defaultCountry, inputValue } = params;

  if (countries.length === 0) {
    return {
      initialCountry: { code: '', dialCode: '', name: '' },
      formattedValue: inputValue || '',
    };
  }

  let resolvedCountry: CountryOption;
  let finalFormattedValue: string;

  if (inputValue) {
    const { country: countryFromVal, preFormattedValue } = findCountryFromInput(
      inputValue,
      countries
    );
    if (countryFromVal) {
      // If country is found from input, use the preFormattedValue from AsYouType directly.
      resolvedCountry = countryFromVal;
      finalFormattedValue = preFormattedValue;
    } else {
      // Country not found from input, use default/fallback country.
      // Format the original inputValue with the resolvedCountry.
      resolvedCountry =
        countries.find(({ code }) => code === defaultCountry) ?? countries[0];
      if (canFormatForCountry(resolvedCountry)) {
        try {
          finalFormattedValue = formatPhoneNumber(
            inputValue, // Use original inputValue
            resolvedCountry.code
          );
        } catch (error) {
          // Fallback to non-numeric if formatting fails
          finalFormattedValue = filterNonNumeric(inputValue);
        }
      } else {
        // Cannot format, just filter non-numeric characters
        finalFormattedValue = filterNonNumeric(inputValue);
      }
    }
  } else {
    // No inputValue, use default/fallback country and its dial code.
    resolvedCountry =
      countries.find(({ code }) => code === defaultCountry) ?? countries[0];
    if (canFormatForCountry(resolvedCountry)) {
      finalFormattedValue = sanitizeDialCode(getDialCode(resolvedCountry));
    } else {
      finalFormattedValue = ''; // No input, cannot format, so empty.
    }
  }

  return {
    initialCountry: resolvedCountry,
    formattedValue: finalFormattedValue,
  };
};

type UsePhoneInputProps = {
  anchorEl: RefObject<HTMLElement | null>;
  countries: CountryOption[];
  defaultCountry?: string;
  defaultValue?: string;
  value?: string;
  onInputChange?: (formattedNumber: string) => void;
};

export const usePhoneInput = ({
  anchorEl: anchorElProp,
  countries,
  defaultCountry,
  defaultValue,
  onInputChange,
  value,
}: UsePhoneInputProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const previousValueRef = useRef<string>('');
  const rawDigitsRef = useRef<string>(''); // Store raw digits for editing state

  const { scheduleCursorUpdate, storeCursorPosition } = useCursorPosition();

  // Initialize state from props
  const { initialCountry, formattedValue } = useMemo(
    () =>
      resolveInitialValues({
        countries,
        defaultCountry,
        inputValue: value ?? defaultValue ?? '',
      }),
    [countries, defaultCountry, defaultValue, value]
  );

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [formattedPhoneNumber, setFormattedPhoneNumber] =
    useState(formattedValue);

  // Store raw digits from formatted value
  useEffect(() => {
    rawDigitsRef.current = filterNonNumeric(formattedPhoneNumber);
  }, [formattedPhoneNumber]);

  // Determine formatting context
  const formattingContext = useMemo(() => {
    if (countries.length === 0) {
      return { canFormat: false, dialCode: '' };
    }

    const countryDialCode = getDialCode(selectedCountry);
    const validDialCode = isValidDialCodeFormat(countryDialCode)
      ? sanitizeDialCode(countryDialCode)
      : '';

    return {
      canFormat: Boolean(validDialCode && isCountryCode(selectedCountry.code)),
      dialCode: validDialCode,
    };
  }, [countries.length, selectedCountry]);

  // Menu handling
  const onOpen = () => setAnchorEl(anchorElProp.current);
  const onClose = () => setAnchorEl(null);

  // Handle country selection
  const onCountrySelect = (country: CountryOption) => {
    setSelectedCountry(country);

    const dialCode = getDialCode(country);
    if (isValidDialCodeFormat(dialCode)) {
      const sanitizedDialCode = sanitizeDialCode(dialCode);
      setFormattedPhoneNumber(sanitizedDialCode);
      onInputChange?.(sanitizedDialCode);
    } else {
      setFormattedPhoneNumber('');
      onInputChange?.('');
    }

    onClose();
  };

  // Store previous value for comparison
  useEffect(() => {
    previousValueRef.current = formattedPhoneNumber;
  }, [formattedPhoneNumber]);

  // Handler for input blur - reapply full formatting
  const handleBlur: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = () => {
    const currentRawDigits = rawDigitsRef.current;
    const currentCountryCode = selectedCountry.code;
    const formattedDialCode = formattingContext.dialCode;
    const dialCodeDigitsOnly = filterNonNumeric(formattedDialCode);

    // Ensure the raw digits include the dial code
    let digitsForFormatting = currentRawDigits;
    if (
      dialCodeDigitsOnly.length > 0 &&
      !digitsForFormatting.startsWith(dialCodeDigitsOnly)
    ) {
      digitsForFormatting = dialCodeDigitsOnly + digitsForFormatting;
    }

    // Use formatIncompletePhoneNumber with the current country code
    const newFormattedValue = formatIncompletePhoneNumber(
      '+' + digitsForFormatting,
      currentCountryCode as CountryCode
    );

    if (newFormattedValue !== formattedPhoneNumber) {
      setFormattedPhoneNumber(newFormattedValue);
      onInputChange?.(newFormattedValue);
    }
  };

  // Handle phone number input changes
  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = event.target.value;
    const element = event.target;
    const currentCursorInCurrentValue = element.selectionStart ?? 0;
    const previousValue = previousValueRef.current;

    const deletedSpace = isSpaceDeleted(
      previousValue,
      currentValue,
      currentCursorInCurrentValue
    );
    storeCursorPosition(currentCursorInCurrentValue);

    const { canFormat, dialCode: formattedDialCodeString } = formattingContext;
    const dialCodeDigits = filterNonNumeric(formattedDialCodeString);

    if (!canFormat) {
      const numericOnly = parseIncompletePhoneNumber(currentValue);
      setFormattedPhoneNumber(numericOnly);
      onInputChange?.(numericOnly);
      return;
    }

    if (deletedSpace) {
      setFormattedPhoneNumber(currentValue);
      if (element && currentCursorInCurrentValue !== null) {
        scheduleCursorUpdate(element, currentCursorInCurrentValue);
      }
      return;
    }

    // Get previous national part if it exists (for preservation if needed)
    let previousNationalPart = '';
    if (
      previousValue.startsWith(formattedDialCodeString) &&
      previousValue.length > formattedDialCodeString.length
    ) {
      previousNationalPart = previousValue.substring(
        formattedDialCodeString.length
      );
    }

    // Extract national part from current input
    let nationalDigits = '';
    let nationalTextForCursor = '';
    let cursorInNationalText = 0;

    // Special case: Check for empty or significantly shorter value (select-all delete case)
    // or when current value is exactly the dial code digits (without the '+')
    if (
      currentValue === '' ||
      (previousValue.length > formattedDialCodeString.length &&
        currentValue.length < formattedDialCodeString.length) ||
      filterNonNumeric(currentValue) === dialCodeDigits
    ) {
      // Just preserve the dial code
      nationalDigits = '';
      nationalTextForCursor = '';
      cursorInNationalText = 0;
    }
    // CASE 1: Input starts with the full formatted dial code (e.g. "+61")
    else if (currentValue.startsWith(formattedDialCodeString)) {
      // Simple case - just extract everything after the dial code
      nationalTextForCursor = currentValue.substring(
        formattedDialCodeString.length
      );
      cursorInNationalText = Math.max(
        0,
        currentCursorInCurrentValue - formattedDialCodeString.length
      );
      nationalDigits = parseIncompletePhoneNumber(nationalTextForCursor);
    }
    // CASE 2: Input is missing or has modified dial code
    else {
      // Check if this is a deletion within dial code area
      const isDeletionWithinDialCode =
        currentValue.length < previousValue.length && // Deletion occurred
        previousValue.startsWith(formattedDialCodeString) && // Previous value had proper dial code
        currentCursorInCurrentValue <= formattedDialCodeString.length; // Cursor position indicates dial code area edit

      // For dial code deletion/modification, preserve the previous national part
      if (isDeletionWithinDialCode && previousNationalPart) {
        nationalTextForCursor = previousNationalPart;
        cursorInNationalText = 0; // Position cursor at start of national part
        nationalDigits = parseIncompletePhoneNumber(previousNationalPart);
      }
      // For digit typed into dial code area, append to existing national number
      else if (currentValue.length === previousValue.length + 1) {
        const typedCharPosition = currentCursorInCurrentValue - 1;
        if (
          typedCharPosition >= 0 &&
          typedCharPosition < formattedDialCodeString.length &&
          /\d/.test(currentValue[typedCharPosition])
        ) {
          // Get previous national digits
          const prevNationalDigits =
            parseIncompletePhoneNumber(previousNationalPart);

          // Add the typed digit to the national part
          const typedDigit = currentValue[typedCharPosition];
          nationalDigits = prevNationalDigits + typedDigit;
          nationalTextForCursor = nationalDigits;
          cursorInNationalText = nationalDigits.length; // Position cursor at the end
        } else {
          // Handle normal typing outside dial code area
          nationalDigits = parseIncompletePhoneNumber(currentValue);
          nationalTextForCursor = currentValue;
          cursorInNationalText = currentCursorInCurrentValue;
        }
      }
      // All other cases: treat everything as the national part
      else {
        nationalDigits = parseIncompletePhoneNumber(currentValue);
        nationalTextForCursor = currentValue;
        cursorInNationalText = currentCursorInCurrentValue;
      }
    }

    // Always use the country's dial code + extracted national digits
    const fullNumber = dialCodeDigits + nationalDigits;

    // Only block if we're adding length and the result would be too long
    // Always allow deletions to prevent users from getting locked in a state
    // Check if this is likely a paste operation (multiple digits added at once)
    const previousRawDigits = filterNonNumeric(previousValue);
    const digitDifference = fullNumber.length - previousRawDigits.length;
    const isProbablyPaste = digitDifference > 1;
    const isGettingLonger = fullNumber.length > previousRawDigits.length;

    // Only block single character typing if we're already at max or would exceed max
    // Allow paste operations even if they exceed max length
    if (isGettingLonger && !isProbablyPaste) {
      // Check if we're already at max length OR if the new value would be too long
      const previousWasTooLong = isNumberTooLong(previousRawDigits);
      const newWouldBeTooLong = isNumberTooLong(fullNumber);

      if (previousWasTooLong || newWouldBeTooLong) {
        return;
      }
    }

    try {
      // Format the number using AsYouType for proper national formatting
      const asYouType = new AsYouType(selectedCountry.code as CountryCode);
      let formattedNumber;

      if (nationalDigits) {
        // Format with country code + national digits
        formattedNumber = asYouType.input('+' + fullNumber);
      } else {
        // Just the country code
        formattedNumber = formattedDialCodeString;
      }

      if (element && currentCursorInCurrentValue !== null) {
        // Calculate cursor position based on digit counts
        const digitsCountInDialCode = dialCodeDigits.length;
        const digitsCountInUserNationalInput = countDigitsBeforeCursor(
          nationalTextForCursor,
          cursorInNationalText
        );
        const totalTargetDigitsBeforeCursor =
          digitsCountInDialCode + digitsCountInUserNationalInput;

        const newCursorPos = calculateCursorPosition(
          formattedNumber,
          totalTargetDigitsBeforeCursor,
          formattedDialCodeString,
          dialCodeDigits
        );
        scheduleCursorUpdate(element, newCursorPos);
      }

      if (formattedNumber !== formattedPhoneNumber) {
        setFormattedPhoneNumber(formattedNumber);
        onInputChange?.(formattedNumber);
      }
    } catch (error) {
      // Fallback to simpler formatting
      const fallbackFormatted =
        formattedDialCodeString + (nationalDigits ? ' ' + nationalDigits : '');

      if (fallbackFormatted !== formattedPhoneNumber) {
        setFormattedPhoneNumber(fallbackFormatted);
        onInputChange?.(fallbackFormatted);
      }
    }
  };

  // Helper function to check if number is too long
  // Expects digits-only string (no '+' prefix or other formatting)
  const isNumberTooLong = (digits: string): boolean => {
    if (!digits) {
      return false;
    }

    // Ensure we're working with digits only and construct E.164 format
    const digitsOnly = filterNonNumeric(digits);
    const testNumber = '+' + digitsOnly;
    // If the selected country is one of the custom codes that exist in our Location Service (data from Booking.com)
    // then map it to the corresponding valid ISO code
    const countryCode =
      CUSTOM_CODE_TO_ISO_CODE_MAP[selectedCountry.code] || selectedCountry.code;

    // Use validatePhoneNumberLength directly with the phone number string and country code
    // This works even for numbers that can't be fully parsed
    if (isCountryCode(countryCode)) {
      const lengthStatus = validatePhoneNumberLength(
        testNumber,
        countryCode as CountryCode
      );
      return lengthStatus === 'TOO_LONG';
    }

    // If we don't have a valid country code, we can't reliably validate length
    // In this case, allow the input rather than block incorrectly
    return false;
  };

  return {
    anchorEl,
    formattedPhoneNumber,
    onClose,
    onCountrySelect,
    onOpen,
    onPhoneNumberChange,
    handleBlur,
    selectedCountry,
  };
};
